import { BigNumber } from 'ethers';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import { Text, Button, Icon } from '@tectonicfi/tectonic-ui-kit';

import Card, { CardProps } from '@components/Card';
import DataRow from '@components/DataRow';
import {
  formatPercent,
  formatRateToPercent,
  formatUserTotalUsdValue,
  formatUserUnderlyingAmount,
  formatUserUnderlyingAmountWithDust,
} from '@lib/units';
import {
  getBorrowDailyInterestAmount,
  getBorrowDailyInterestRate,
} from '@lib/math';
import useDisableBorrow from '@hooks/useDisableBorrow';
import { PoolType } from '@config/base';
import { BLACKLIST_SHORT_TOKENS } from '@config/constants';

import CardValue from './CardValue';

export interface YourBorrowCardProps {
  mode: PoolType;
  amount: BigNumber | null;
  asset: TectonicAsset;
  availableBorrowAmount: BigNumber | null;
  borrowLimit: BigNumber | null;
  borrowRatePerBlock: BigNumber | null;
  cardProps?: Omit<CardProps, 'children'>;
  hasSuppliedAssetOnly?: boolean;
  loading: boolean;
  onShort(): void;
  onBorrow(): void;
  onRepay(): void;
  usdPrice: BigNumber | null;
}

function YourBorrowCard({
  mode,
  amount,
  asset,
  availableBorrowAmount,
  borrowLimit,
  borrowRatePerBlock,
  cardProps,
  hasSuppliedAssetOnly,
  loading,
  onShort,
  onBorrow,
  onRepay,
  usdPrice,
}: YourBorrowCardProps): JSX.Element {
  const dailyInterestAmount =
    amount && borrowRatePerBlock
      ? getBorrowDailyInterestAmount(borrowRatePerBlock, amount)
      : null;
  const dailyInterestRate =
    amount && dailyInterestAmount && usdPrice
      ? getBorrowDailyInterestRate(asset, dailyInterestAmount, amount, usdPrice)
      : null;

  const disableBorrow = useDisableBorrow(mode, asset);
  return (
    <Card {...(cardProps ?? {})}>
      <Text variant="heading3" className="mb-3">
        Your borrow
      </Text>
      <div className="space-y-2">
        <DataRow
          label={<Text>Currently borrowing</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                amount && (
                  <Text className="font-semibold">
                    {`${formatUserUnderlyingAmountWithDust(asset, amount)} ${
                      asset.symbol
                    }`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Daily interest</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                !!(dailyInterestAmount && dailyInterestRate) && (
                  <Text className="font-semibold">
                    {`${formatUserUnderlyingAmountWithDust(
                      asset,
                      dailyInterestAmount
                    )} ${asset.symbol} (${formatPercent(
                      formatRateToPercent(dailyInterestRate)
                    )})`}
                  </Text>
                )
              }
            />
          }
        />
        <DataRow
          label={<Text>Borrow limit</Text>}
          value={
            <CardValue
              loading={loading}
              value={
                borrowLimit && (
                  <Text className="font-semibold">
                    {formatUserTotalUsdValue(borrowLimit)}
                  </Text>
                )
              }
            />
          }
        />
        {!hasSuppliedAssetOnly && (
          <DataRow
            label={<Text>Available borrow</Text>}
            value={
              <CardValue
                loading={loading}
                value={
                  availableBorrowAmount && (
                    <Text className="font-semibold">
                      {`${formatUserUnderlyingAmount(
                        asset,
                        availableBorrowAmount
                      )} ${asset.symbol}`}
                    </Text>
                  )
                }
              />
            }
          />
        )}
      </div>

      <div className="mt-3 flex items-center justify-center gap-7 desktop:flex-row mobile:gap-2">
        {!BLACKLIST_SHORT_TOKENS.includes(asset.symbol) && (
          <Button
            className="!border-0 !px-0 mobile:flex-1"
            disabled={loading || hasSuppliedAssetOnly}
            theme="pink"
            onClick={onShort}
            size="small"
            variant="outlined"
          >
            <div>
              <Icon.Short className="#FF96DF inline" width="32" height="32" />
              <div className="text-xs font-light">Short</div>
            </div>
          </Button>
        )}
        <Button
          className="!border-0 !px-0 mobile:flex-1"
          disabled={loading || hasSuppliedAssetOnly || disableBorrow}
          size="small"
          theme="pink"
          variant="outlined"
          onClick={onBorrow}
        >
          <div>
            <Icon.Borrow className="#FF96DF inline" width="32" height="32" />
            <div className="text-xs font-light">Borrow</div>
          </div>
        </Button>

        <Button
          className="!border-0 !px-0 mobile:flex-1"
          disabled={loading || hasSuppliedAssetOnly}
          size="small"
          theme="pink"
          variant="outlined"
          onClick={onRepay}
        >
          <div>
            <Icon.Repay className="#FF96DF inline" width="32" height="32" />
            <div className="text-xs font-light">Repay</div>
          </div>
        </Button>
      </div>
      {disableBorrow && (
        <Text className="mt-0.5 text-small">
          You are unable to borrow from this market as the maximum amount that
          can be borrowed has been reached.
        </Text>
      )}
      {hasSuppliedAssetOnly && (
        <div className="mt-2 flex gap-1">
          <div>
            <Icon.Info className="text-yellowPrimary" width={18} height={18} />
          </div>
          <Text className="text-mediumSmall">
            Since you have already supplied {asset.symbol}, borrowing is
            disabled.
          </Text>
        </div>
      )}
    </Card>
  );
}

export default YourBorrowCard;
